/*************************************************************************
 *
 * c20g CONFIDENTIAL
 * __________________
 *
 *  [2007] - [2021] Counterpoint Consulting, Incorporated
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Counterpoint Consulting Incorporated.
 * The intellectual and technical concepts contained
 * herein are proprietary to Counterpoint Consulting Incorporated
 * and its suppliers and may be covered by U.S. and Foreign Patents,
 * patents in process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Counterpoint Consulting Incorporated.
 */

import _ from 'lodash';
import Radio from 'backbone.radio';
import Backgrid from 'backgrid'

import PageableCollection from '@/backbone/PageableCollection';
import ButtonCell from '@/backgrid/ButtonCell';
import StringCell from '@/backgrid/StringCell';
import ExpandableCell from '@/backgrid/ExpandableCell';
import { DateCell } from '@/backgrid/MomentCell';
import GridView from '@/views/GridView';
import { userHasRole } from '@/util/RoleUtils';
import showSuccessMessage from '@/util/ShowSuccessMessage';
import OrderPackageStatuses from '@/enums/OrderPackageStatuses';

import ClaimOrderPackage from '../../services/ClaimOrderPackage';

const InProgressOrderPackagesGridView = GridView.extend({
  className: 'in-progress-order-package-grid horizontal-scroll',

  initialize() {
    const self = this;
    this.options.columns = [
      {
        name: 'orderPackageId',
        label: 'Order Package ID',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'orderIds',
        label: 'Packaged Orders',
        cell: ExpandableCell,
        headerCell: 'custom',
        width: 12,
        editable: false,
      },
      {
        name: 'regionGroup',
        label: 'Branch',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'assignedEpc',
        label: 'Assigned EPC',
        cell: StringCell,
        headerCell: 'custom',
        width: 18,
        editable: false,
        renderable: userHasRole('EPC')
      },
      {
        name: 'assignedCrs',
        label: 'Assigned CRS',
        cell: StringCell,
        headerCell: 'custom',
        width: 15,
        editable: false
      },
      {
        name: 'orderType',
        label: 'Order Type',
        cell: StringCell,
        headerCell: 'custom',
        width: 19,
        editable: false
      },
      {
        name: 'status',
        label: 'Status',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'replacementPIINSPIIN',
        label: 'Contract #',
        cell: StringCell,
        headerCell: 'custom',
        width: 12,
        editable: false
      },
      {
        name: 'popStartDate',
        label: 'POP Start Date',
        cell: DateCell,
        headerCell: 'custom',
        width: 11,
        editable: false
      },
      {
        name: 'dateCreated',
        label: 'Created On',
        cell: DateCell,
        headerCell: 'custom',
        width: 11,
        editable: false,
        direction: 'ascending'
      },
      {
        name: 'openClaimButton',
        label: '',
        width: 8,
        headerCell: 'custom',
        cell: ButtonCell.extend({
          name() {
            return this.model.get('statusId') === OrderPackageStatuses.EPC_PENDING_ASSIGNMENT && userHasRole('EPC')
              ? 'Claim'
              : 'Open';
          },
          onClick(e, model) {
            this.model.get('statusId') === OrderPackageStatuses.EPC_PENDING_ASSIGNMENT && userHasRole('EPC')
              ? self.onClickClaim(e, model)
              : self.viewDetails(e, model);
          },
          buttonClass() {
            return this.model.get('statusId') === OrderPackageStatuses.EPC_PENDING_ASSIGNMENT && userHasRole('EPC')
              ? 'btn-sm btn-outline-success text-white-hover'
              : 'btn-sm btn-outline-primary';
          }
        }),
        editable: false,
        sortable: false
      }
    ];
    const orderPackages = this.model.get('inProgressOrderPackages');
    this.padOrderPackageIds(orderPackages);
    this.options.collection = new PageableCollection(orderPackages, { pageSize: 10 });
    this.options.emptyText = 'No In-Progress Order Packages';
  },

  padOrderPackageIds(orderPackages) {
    _.each(orderPackages, (op) => (op.orderPackageId = op.orderPackageId.padStart(5, '0')));
  },

  async onClickClaim(e, model) {
    e.preventDefault();
    try {
      await ClaimOrderPackage(model.get('orderPackageId'));

      showSuccessMessage({
        code: 'claimSuccess',
        title: 'Claim Success',
        message: 'Order Package Claimed.',
        hideAfter: 1
      });

      Radio.channel('navigate').request('show:int:order-package', model.get('orderPackageId'));
    } catch (err) {
      // Ignore
    }
  },

  viewDetails(e, model) {
    e.preventDefault();
    Radio.channel('navigate').request('show:int:order-package', model.get('orderPackageId'));
  }
});

export default InProgressOrderPackagesGridView;
